import { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Stack,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as uiSlice from '../../../data/ui.slice';
import * as clientSlice from '../../../data/client.slice';
import {
  Operand,
  TransportationBenefitModel,
} from '../../../data/models/transportation-benefit-model.model';
import TextInput from '../../forms/TextInput';
import TBMOperandEditor from './TBMOperandEditor';

const PREFIX = 'TransportationBenefitModelDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
  actionButton: `${PREFIX}-actionButton`,
  closeButton: `${PREFIX}-closeButton`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: 450,
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    gap: theme.spacing(3),
  },

  [`& .${classes.actions}`]: {
    display: 'flex',
    justifyContent: 'end',
    paddingBottom: theme.spacing(5),
  },
  [`& .${classes.actionButton}`]: {
    height: 48,
  },
  [`& .${classes.closeButton}`]: {
    width: 180,
  },
}));

interface TransportationBenefitModelDialogProps {
  transportationBenefitModel?: TransportationBenefitModel;
  isReadOnly?: boolean;
  open: boolean;
  close: () => void;
}

export default function TransportationBenefitModelDialog({
  transportationBenefitModel,
  isReadOnly = false,
  open,
  close,
}: TransportationBenefitModelDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const savingTransportationBenefitModel = useAppSelector(
    clientSlice.selectSavingTransportationBenefitModel,
  );
  const savedTransportationBenefitModel = useAppSelector(
    clientSlice.selectSavedTransportationBenefitModel,
  );
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);

  const form = useForm({
    defaultValues: {
      transportationBenefitModelUUID: undefined as string | undefined,
      name: '',
      expression: { literal: true } as Operand,
    },
  });

  useEffect(() => {
    if (!transportationBenefitModel) {
      return;
    }
    form.setValue(
      'transportationBenefitModelUUID',
      transportationBenefitModel?.transportationBenefitModelUUID,
    );
    form.setValue('name', transportationBenefitModel?.name || '');
    form.setValue(
      'expression',
      transportationBenefitModel?.expression || { literal: true },
    );
    form.trigger();
  }, [transportationBenefitModel]);

  useEffect(() => {
    if (saveSuccessMessage && savedTransportationBenefitModel) {
      handleClose();
      dispatch(clientSlice.setSavedTransportationBenefitModel(false));
    }
  }, [saveSuccessMessage]);

  const handleSave = (data: any) => {
    if (data.transportationBenefitModelUUID) {
      dispatch(clientSlice.updateTransportationBenefitModelAsync(data));
    } else {
      dispatch(clientSlice.createTransportationBenefitModelAsync(data));
    }
  };

  const handleClose = () => {
    form.reset();
    close();
  };

  let title = '';
  if (!transportationBenefitModel?.transportationBenefitModelUUID) {
    title = t('transBenefitModel.addTransBenefitModel');
  } else {
    if (isReadOnly) {
      title = t('transBenefitModel.viewTransBenefitModel');
    } else {
      title = t('transBenefitModel.editTransBenefitModel');
    }
  }

  return (
    <RootDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth={true}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <TextInput
            name="name"
            label={t('transBenefitModel.transBenefitModelName')}
            readOnly={isReadOnly}
            control={form.control}
            required={true}
            rules={{ required: requiredFieldMessage }}
            errors={form.formState.errors}
          />
          <TBMOperandEditor
            operand={form.watch('expression') as any}
            onChange={(data) => form.setValue('expression', data)}
            isReadOnly={isReadOnly}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        {isReadOnly && (
          <Stack direction="row" justifyContent="end">
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={`${classes.actionButton} ${classes.closeButton}`}
              onClick={handleClose}>
              {t('common.close')}
            </Button>
          </Stack>
        )}
        {!isReadOnly && (
          <>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              className={classes.actionButton}
              disabled={savingTransportationBenefitModel}
              onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.actionButton}
              disabled={
                !form.formState.isValid || savingTransportationBenefitModel
              }
              onClick={form.handleSubmit(handleSave)}>
              {transportationBenefitModel ? t('common.save') : t('common.add')}
            </Button>
          </>
        )}
      </DialogActions>
    </RootDialog>
  );
}
